import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ThemeProvider, useTheme, Topbar, SectionCard,
  EmptyState, StatusBadge
} from '../shared/DashWidgets';
import Sidebar from '../shared/Sidebar';
import { buildStyles, COLORS } from '../shared/DashTheme';
import api from '../../services/api';

// Authority only sees: all assigned (includes in-progress), resolved, not-resolved
const TABS = [
  { key: 'all',          label: 'All Assigned',   color: COLORS.purple },
  { key: 'assigned',     label: 'In Progress',    color: COLORS.cyan   },
  { key: 'resolved',     label: 'Resolved',       color: COLORS.green  },
  { key: 'not_resolved', label: 'Not Resolved',   color: COLORS.amber  },
];

function AuthorityComplaintsHistoryInner() {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    // All tabs fetch from assigned-complaints (authority's scope)
    // "all" = everything assigned to authority
    // "assigned" = verified/in-progress (not yet resolved)
    // "resolved" = resolved ones
    // "not_resolved" = assigned but not resolved (same as in-progress)
    const endpoint = tab === 'resolved'
      ? '/authority/resolved-complaints'
      : '/authority/assigned-complaints';

    api.get(endpoint)
      .then(res => {
        let data = Array.isArray(res.data) ? res.data : [];
        if (tab === 'assigned')     data = data.filter(c => c.status !== 'resolved');
        if (tab === 'not_resolved') data = data.filter(c => c.status !== 'resolved');
        setComplaints(data);
      })
      .catch(() => setComplaints([]))
      .finally(() => setLoading(false));
  }, [tab]);

  const filtered = complaints.filter(c =>
    !search || (c.title || '').toLowerCase().includes(search.toLowerCase())
      || (c.student_name || '').toLowerCase().includes(search.toLowerCase())
      || (c.department || '').toLowerCase().includes(search.toLowerCase())
  );

  const inp = {
    padding: '9px 14px', borderRadius: 9, fontSize: 13,
    background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
    color: '#fff', outline: 'none', width: 240,
  };

  return (
    <div style={s.page}>
      <div style={s.bgGrid} /><div style={s.bgOrb1} /><div style={s.bgOrb2} /><div style={s.bgOrb3} />
      <div style={s.layout}>
        {sidebarOpen && <Sidebar onToggle={() => setSidebarOpen(o => !o)} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />}
        <div style={{ ...s.main, marginLeft: isMobile ? 0 : (sidebarOpen ? 248 : 0), transition: 'margin-left 0.3s ease' }}>
          <Topbar
            title="Complaints History"
            subtitle="Complaints assigned to authority — in progress and resolved"
            onToggleSidebar={() => setSidebarOpen(o => !o)}
            onMobileMenu={() => setMobileOpen(true)}
            actions={
              <button className="ur-btn-hover" style={s.btnSecondary} onClick={() => navigate('/dashboard')}>
                Dashboard
              </button>
            }
          />
          <div style={s.content}>
            {/* Summary counts — always visible */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
              {[
                ['All Assigned', complaints.length, COLORS.purple],
                ['In Progress',  complaints.filter(c => c.status !== 'resolved').length, COLORS.cyan],
                ['Resolved',     complaints.filter(c => c.status === 'resolved').length, COLORS.green],
              ].map(([label, cnt, color]) => (
                <div key={label} style={{
                  padding: '8px 18px', borderRadius: 10,
                  background: 'rgba(255,255,255,0.08)', border: `1px solid ${color}33`,
                  borderTop: `3px solid ${color}`,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 100,
                }}>
                  <span style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>{cnt}</span>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>{label}</span>
                </div>
              ))}
            </div>

            <SectionCard title="Complaint List" subtitle={`${filtered.length} records`} delay="0.05s"
              action={
                <input
                  style={inp}
                  placeholder="Search title, student, dept..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              }>
              {/* Tabs */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
                {TABS.map(t => (
                  <button key={t.key} className="ur-btn-hover"
                    onClick={() => setTab(t.key)}
                    style={{
                      padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                      cursor: 'pointer', border: 'none',
                      background: tab === t.key ? t.color : 'rgba(255,255,255,0.1)',
                      color: tab === t.key ? '#fff' : 'rgba(255,255,255,0.65)',
                      boxShadow: tab === t.key ? `0 2px 10px ${t.color}55` : 'none',
                    }}>
                    {t.label}
                  </button>
                ))}
              </div>

              {loading
                ? <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.4)' }}>Loading...</div>
                : filtered.length === 0
                ? <EmptyState text="No complaints found" sub="Try a different filter or search" />
                : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={s.table}>
                      <thead style={s.thead}>
                        <tr>
                          <th style={s.th}>Title</th>
                          <th style={s.th}>Status</th>
                          <th style={s.th}>Department</th>
                          <th style={s.th}>Student</th>
                          <th style={s.th}>Submitted</th>
                          <th style={s.th}>Resolved</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map(c => (
                          <tr key={c.id} className="ur-row-hover"
                            onClick={() => navigate(`/authority/resolve/${c.id}`)}>
                            <td style={s.tdBold}>{c.title}</td>
                            <td style={s.td}><StatusBadge status={c.status} /></td>
                            <td style={s.td}><span style={{ fontSize: 12 }}>{c.department || '—'}</span></td>
                            <td style={s.td}><span style={{ fontSize: 12 }}>{c.student_name || '—'}</span></td>
                            <td style={s.td}><span style={{ fontSize: 12 }}>{c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}</span></td>
                            <td style={s.td}><span style={{ fontSize: 12 }}>{c.resolved_at ? new Date(c.resolved_at).toLocaleDateString() : '—'}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              }
            </SectionCard>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AuthorityComplaintsHistory() {
  return <ThemeProvider><AuthorityComplaintsHistoryInner /></ThemeProvider>;
}
